import { useState, useEffect, useRef } from "react";
import { useAppContext } from "@/app/providers/AppContext";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { Badge } from "@/shared/ui/badge";
import {
  IconArrowUpRight,
  IconBrandSlack,
  IconBrandTelegram,
  IconBrandWhatsapp,
  IconCheck,
  IconLoader2,
  IconLock,
  IconMail,
  IconMessage2Check,
  IconPointFilled,
} from "@tabler/icons-react";
import { Mono } from "@/shared/ui/DesignLib";
import { PortalPageSkeleton } from "@/shared/components/PortalPageSkeletons";
import type { NotificationChannelConfig, UserPrefs } from "@/shared/lib/types";

type ChannelKey = "slack" | "telegram" | "email" | "sms" | "whatsapp";
type NotificationChannelState = NotificationChannelConfig & {
  webhook_url?: string;
  chat_id?: string;
  address?: string;
  phone_number?: string;
};
type NotificationPreferences = UserPrefs & {
  channels: Record<ChannelKey, NotificationChannelState>;
};

interface TelegramStatusResponse {
  connected?: boolean;
}

interface TelegramLinkResponse {
  token: string;
}

interface NotificationTestResponse {
  success?: boolean;
  error?: string;
  message?: string;
}

interface ChannelDescriptor {
  key: ChannelKey;
  Icon: typeof IconMail;
  title: string;
  desc: string;
}

interface NotificationsTabProps {
  /** Navigates to the plans page; wired by the dashboard shell. */
  onUpgrade?: () => void;
}

function clonePreferences<T>(preferences: T): T {
  return JSON.parse(JSON.stringify(preferences)) as T;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const NotificationsTab = ({ onUpgrade }: NotificationsTabProps) => {
  const { api, prefs, setPrefs, profile } = useAppContext();
  const plan = (profile?.plan || "trial").toLowerCase();
  const profileEmail = profile?.email || "";

  // Per-plan channel access rules
  const planChannelAccess: Record<string, Record<ChannelKey, boolean>> = {
    trial: { slack: true, telegram: true, email: false, sms: false, whatsapp: false },
    toolbox: { slack: false, telegram: false, email: false, sms: false, whatsapp: false },
    oracle: { slack: true, telegram: true, email: true, sms: true, whatsapp: true },
    oracle_monitor: { slack: true, telegram: true, email: true, sms: true, whatsapp: true },
  };
  const allowed = planChannelAccess[plan] ?? {
    slack: true,
    telegram: true,
    email: true,
    sms: true,
    whatsapp: true,
  };

  const planLabel: Record<string, string> = {
    trial: "Trial",
    toolbox: "Toolbox",
    oracle: "Oracle",
    oracle_monitor: "Oracle Monitor",
  };

  const activePrefs: NotificationPreferences = (prefs as NotificationPreferences | null) || {
    channels: {
      slack: { enabled: false, webhook_url: "" },
      telegram: { enabled: false, chat_id: "" },
      email: { enabled: false, address: "" },
      sms: { enabled: false, phone_number: "" },
      whatsapp: { enabled: false, phone_number: "" },
    },
  };

  const [loading, setLoading] = useState(!prefs);
  const [isTelegramLinking, setIsTelegramLinking] = useState(false);
  const [telegramToken, setTelegramToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [testingChannel, setTestingChannel] = useState<string | null>(null);
  const savedPrefsRef = useRef<NotificationPreferences | null>(
    prefs ? clonePreferences(prefs as NotificationPreferences) : null,
  );
  const telegramChatId = activePrefs.channels.telegram?.chat_id;

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
      return;
    }
    toast.info("Upgrade to the Oracle plan to unlock alert delivery.");
  };

  useEffect(() => {
    if (!hasChanges && prefs?.channels) {
      savedPrefsRef.current = clonePreferences(prefs as NotificationPreferences);
    }
  }, [hasChanges, prefs]);

  useEffect(() => {
    if (!profileEmail) return;

    const currentEmail = prefs?.channels?.email?.address || "";
    if (currentEmail === profileEmail) return;

    setPrefs({
      ...((prefs as NotificationPreferences | null) || activePrefs),
      channels: {
        ...(prefs?.channels || activePrefs.channels),
        email: {
          ...(prefs?.channels?.email || activePrefs.channels.email),
          address: profileEmail,
        },
      },
    });
  }, [activePrefs, prefs, profileEmail, setPrefs]);

  useEffect(() => {
    const fetchPrefs = async () => {
      if (prefs) {
        setLoading(false);
        return;
      }
      try {
        const data = await api<NotificationPreferences>("GET", "/api/notifications/preferences");
        if (data?.channels) {
          savedPrefsRef.current = clonePreferences(data);
          setPrefs(data);
        }
      } catch (e) {}
      setLoading(false);
    };
    fetchPrefs();
  }, []);

  useEffect(() => {
    if (!isTelegramLinking) return;

    // If preferences already show a linked chat, stop the link flow immediately.
    if (telegramChatId) {
      setIsTelegramLinking(false);
      setTelegramToken("");
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const maxAttempts = 30; // 60s at 2s polling

    const pollStatus = async () => {
      attempts += 1;
      try {
        const status = await api<TelegramStatusResponse>("GET", "/api/notifications/telegram/status");
        if (status?.connected) {
          const latest = await api<NotificationPreferences>("GET", "/api/notifications/preferences");
          if (!cancelled && latest?.channels) {
            savedPrefsRef.current = clonePreferences(latest);
            setPrefs(latest);
            setHasChanges(false);
            setIsTelegramLinking(false);
            setTelegramToken("");
            toast.success("Telegram connected successfully");
          }
          if (intervalId) clearInterval(intervalId);
          return;
        }
      } catch (e) {}

      if (attempts >= maxAttempts) {
        if (!cancelled) {
          setIsTelegramLinking(false);
          toast.info(
            "Link still pending. Send /link TOKEN in Telegram and try again.",
          );
        }
        if (intervalId) clearInterval(intervalId);
      }
    };

    void pollStatus();
    intervalId = setInterval(() => {
      void pollStatus();
    }, 2000);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [api, isTelegramLinking, setPrefs, telegramChatId]);

  const handleUpdate = async (field: ChannelKey, subfield: string, value: unknown) => {
    const newPrefs = { ...activePrefs };
    newPrefs.channels = { ...newPrefs.channels };
    newPrefs.channels[field] = {
      ...newPrefs.channels[field],
      [subfield]: value,
    };
    setPrefs(newPrefs);
    setHasChanges(true);
  };

  // Guard: show a toast and bail if the channel isn't included in the current plan
  const handleToggle = (channelKey: ChannelKey, value: boolean) => {
    if (value && !allowed[channelKey]) {
      const currentLabel = planLabel[plan] ?? plan;
      if (
        !allowed.slack &&
        !allowed.telegram &&
        !allowed.email &&
        !allowed.sms &&
        !allowed.whatsapp
      ) {
        toast.info(
          `Notifications are not available on the ${currentLabel} plan. Upgrade to Oracle or higher.`,
        );
      } else {
        const channelName = channelKey.toUpperCase();
        toast.info(
          `${channelName} notifications are not available on the ${currentLabel} plan. Upgrade to Oracle or higher.`,
        );
      }
      return; // don't apply the change
    }
    handleUpdate(channelKey, "enabled", value);
  };

  const savePreferences = async (preferences: NotificationPreferences = activePrefs) => {
    setSaving(true);
    try {
      const normalizedPreferences = {
        ...preferences,
        channels: {
          ...preferences.channels,
          email: {
            ...preferences.channels.email,
            address: profileEmail || preferences.channels.email?.address || "",
          },
        },
      };
      const updated = await api<NotificationPreferences>(
        "PUT",
        "/api/notifications/preferences",
        normalizedPreferences,
      );
      if (updated?.channels) {
        savedPrefsRef.current = clonePreferences(updated);
        setPrefs(updated);
      }
      setHasChanges(false);
      toast.success("Preferences saved successfully");
      return true;
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to save preferences"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await savePreferences();
  };

  const handleDiscard = () => {
    if (savedPrefsRef.current) {
      setPrefs(clonePreferences(savedPrefsRef.current));
    }
    setHasChanges(false);
  };

  const linkTelegram = async () => {
    try {
      const data = await api<TelegramLinkResponse>("POST", "/api/notifications/telegram/link");
      setTelegramToken(data.token);
      setIsTelegramLinking(true);
      toast.info("Follow the instructions below to complete linking");
    } catch (e) {
      toast.error("Failed to start Telegram link");
    }
  };

  // Check if any channel is configured
  const hasAnyConfigured =
    (activePrefs.channels.slack?.enabled &&
      activePrefs.channels.slack?.webhook_url) ||
    (activePrefs.channels.telegram?.enabled &&
      activePrefs.channels.telegram?.chat_id) ||
    (activePrefs.channels.email?.enabled &&
      activePrefs.channels.email?.address) ||
    (activePrefs.channels.sms?.enabled &&
      activePrefs.channels.sms?.phone_number) ||
    (activePrefs.channels.whatsapp?.enabled &&
      activePrefs.channels.whatsapp?.phone_number);

  const getChannelConfigError = (channelKey: ChannelKey) => {
    switch (channelKey) {
      case "slack":
        return activePrefs.channels.slack?.webhook_url
          ? ""
          : "Add a Slack webhook URL first.";
      case "telegram":
        return activePrefs.channels.telegram?.chat_id
          ? ""
          : "Link your Telegram account first.";
      case "email":
        return activePrefs.channels.email?.address
          ? ""
          : "Add an email address first.";
      case "sms":
        return activePrefs.channels.sms?.phone_number
          ? ""
          : "Add a phone number first.";
      case "whatsapp":
        return activePrefs.channels.whatsapp?.phone_number
          ? ""
          : "Add a WhatsApp number first.";
      default:
        return "Channel is not configured.";
    }
  };

  const handleTestChannel = async (
    channelKey: ChannelKey,
    channelTitle: string,
  ) => {
    if (!allowed[channelKey]) {
      const currentLabel = planLabel[plan] ?? plan;
      toast.info(
        `${channelTitle} notifications are not available on the ${currentLabel} plan.`,
      );
      return;
    }

    if (!activePrefs.channels[channelKey]?.enabled) {
      toast.info(`Enable ${channelTitle} notifications before sending a test.`);
      return;
    }

    const configError = getChannelConfigError(channelKey);
    if (configError) {
      toast.info(configError);
      return;
    }

    if (hasChanges) {
      const savedOk = await savePreferences();
      if (!savedOk) {
        return;
      }
    }

    setTestingChannel(channelKey);
    try {
      const result = await api<NotificationTestResponse>("POST", `/api/notifications/test/${channelKey}`);
      if (result?.success === false) {
        throw new Error(
          result?.error || `Failed to send ${channelTitle} test notification.`,
        );
      }
      toast.success(
        result?.message ||
          `Test notification sent. Your ${channelTitle} configuration is working properly.`,
      );
    } catch (e: unknown) {
      toast.error(
        errorMessage(e, `Failed to send ${channelTitle} test notification.`),
      );
    } finally {
      setTestingChannel(null);
    }
  };

  if (loading) {
    return <PortalPageSkeleton tabId="notifications" />;
  }

  const channels: ChannelDescriptor[] = [
    {
      key: "slack",
      Icon: IconBrandSlack,
      title: "Slack",
      desc: "Alerts delivered to a Slack channel",
    },
    {
      key: "telegram",
      Icon: IconBrandTelegram,
      title: "Telegram",
      desc: "Alerts from the Linkedhash bot",
    },
    {
      key: "email",
      Icon: IconMail,
      title: "Email",
      desc: "Alerts delivered to your inbox",
    },
    {
      key: "sms",
      Icon: IconMessage2Check,
      title: "SMS",
      desc: "Alerts by text message",
    },
    {
      key: "whatsapp",
      Icon: IconBrandWhatsapp,
      title: "WhatsApp",
      desc: "Alerts by WhatsApp message",
    },
  ];

  const renderChannelContent = (ch: ChannelDescriptor) => {
    switch (ch.key) {
      case "slack":
        return (
          <div className="space-y-1.5">
            <Label
              htmlFor="slack-webhook"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80"
            >
              Webhook URL
            </Label>
            <Input
              id="slack-webhook"
              type="text"
              placeholder="https://hooks.slack.com/services/..."
              value={activePrefs.channels.slack?.webhook_url || ""}
              onChange={(e) =>
                handleUpdate("slack", "webhook_url", e.target.value)
              }
              disabled={!activePrefs.channels.slack?.enabled}
              className="text-sm"
            />
          </div>
        );
      case "telegram":
        return activePrefs.channels.telegram?.chat_id ? (
          <div className="flex items-center gap-2 rounded-lg border border-(--gain)/35 bg-(--gain)/10 px-3 py-2.5">
            <IconPointFilled
              size={13}
              className="motion-safe:animate-pulse text-(--gain)"
              aria-hidden="true"
            />
            <span className="text-sm font-medium text-(--gain)">
              Connected
            </span>
          </div>
        ) : isTelegramLinking ? (
          <div className="space-y-2 rounded-lg border border-(--info)/35 bg-(--info)/10 p-3">
            <p className="text-xs text-muted-foreground">
              Send this command to our bot:
            </p>
            <code className="block rounded border border-border bg-background p-2 font-mono text-xs break-all">
              /link {telegramToken}
            </code>
            <p className="text-xs text-muted-foreground">
              Visit{" "}
              <a
                href="http://t.me/Linkedhash_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-(--info) underline"
              >
                Linkedhash_bot
              </a>{" "}
              on Telegram
            </p>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={linkTelegram}
            size="sm"
            className="w-full"
          >
            Link Telegram Account
          </Button>
        );
      case "email":
        return (
          <div className="space-y-1.5">
            <Label
              htmlFor="email-address"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80"
            >
              Email Address
            </Label>
            <Input
              id="email-address"
              type="email"
              placeholder="No account email found"
              value={profileEmail || activePrefs.channels.email?.address || ""}
              disabled
              className="text-sm"
            />
          </div>
        );
      case "sms":
        return (
          <div className="space-y-1.5">
            <Label
              htmlFor="sms-phone"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80"
            >
              Phone Number
            </Label>
            <Input
              id="sms-phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={activePrefs.channels.sms?.phone_number || ""}
              onChange={(e) =>
                handleUpdate("sms", "phone_number", e.target.value)
              }
              disabled={!activePrefs.channels.sms?.enabled}
              className="text-sm"
            />
          </div>
        );
      case "whatsapp":
        return (
          <div className="space-y-1.5">
            <Label
              htmlFor="whatsapp-phone"
              className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/80"
            >
              WhatsApp Number
            </Label>
            <Input
              id="whatsapp-phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              value={activePrefs.channels.whatsapp?.phone_number || ""}
              onChange={(e) =>
                handleUpdate("whatsapp", "phone_number", e.target.value)
              }
              disabled={!activePrefs.channels.whatsapp?.enabled}
              className="text-sm"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <main className="min-h-[88vh] mx-auto">
      <header className="mb-8">
        <h1
          className="watches-page-title font-bold tracking-tight mb-2 text-foreground"
          style={{ lineHeight: 1.1 }}
        >
          Alert Preferences
        </h1>
        <Mono size=".8rem">Choose where your Watch alerts are delivered.</Mono>
      </header>

      {/* Channel Grid */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {channels.map((ch) => {
          const isEnabled = activePrefs.channels[ch.key]?.enabled || false;
          const channelConfig = activePrefs.channels[ch.key] || {};
          const isConnected =
            !!channelConfig.chat_id ||
            !!channelConfig.address ||
            !!channelConfig.phone_number ||
            !!channelConfig.webhook_url;
          const isLocked = !allowed[ch.key];
          const configError = getChannelConfigError(ch.key);

          return (
            <Card
              key={ch.key}
              className={`flex flex-col transition-colors ${
                isLocked
                  ? "opacity-60"
                  : isEnabled
                    ? "border-(--primary)/40"
                    : ""
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <div
                      className={`grid size-11 shrink-0 place-items-center rounded-lg border ${
                        isEnabled && !isLocked
                          ? "border-(--primary)/30 bg-(--primary)/10"
                          : "bg-muted"
                      }`}
                    >
                      <ch.Icon
                        size={22}
                        stroke={1.75}
                        className={
                          isEnabled && !isLocked
                            ? "text-(--primary)"
                            : "text-muted-foreground"
                        }
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-base">{ch.title}</CardTitle>
                        {isLocked && (
                          <Badge
                            variant="secondary"
                            className="gap-1 text-[10px] font-bold uppercase"
                          >
                            <IconLock size={11} aria-hidden="true" />
                            Oracle plan
                          </Badge>
                        )}
                        {!isLocked && isEnabled && isConnected && (
                          <Badge
                            variant="outline"
                            className="gap-1.5 border-(--gain)/35 bg-(--gain)/10 text-[10px] font-bold uppercase text-(--gain)"
                          >
                            <IconPointFilled
                              size={10}
                              className="motion-safe:animate-pulse text-(--gain)"
                              aria-hidden="true"
                            />
                            Connected
                          </Badge>
                        )}
                        {!isLocked && isEnabled && !isConnected && (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-bold uppercase text-muted-foreground"
                          >
                            Needs setup
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="mt-1 text-xs">
                        {ch.desc}
                      </CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={isLocked ? false : isEnabled}
                    onCheckedChange={(v) => handleToggle(ch.key, v)}
                    disabled={isLocked}
                    aria-label={`Toggle ${ch.title}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-3">
                {isLocked ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Included with the Oracle plan. Upgrade to deliver alerts
                      to {ch.title}.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="self-start"
                      onClick={handleUpgrade}
                    >
                      <IconArrowUpRight size={15} aria-hidden="true" />
                      Upgrade
                    </Button>
                  </>
                ) : (
                  <>
                    <div className={isEnabled ? "" : "pointer-events-none opacity-40"}>
                      {renderChannelContent(ch)}
                    </div>
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs text-muted-foreground">
                        {isEnabled
                          ? configError
                          : `Turn on ${ch.title} to configure delivery.`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestChannel(ch.key, ch.title)}
                        disabled={
                          saving ||
                          testingChannel === ch.key ||
                          !isEnabled ||
                          !!configError
                        }
                      >
                        {testingChannel === ch.key ? (
                          <>
                            <IconLoader2
                              className="mr-1 h-4 w-4 animate-spin"
                              aria-hidden="true"
                            />
                            Sending
                          </>
                        ) : (
                          "Send test"
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sticky save bar while there are unsaved changes; quiet status line otherwise */}
      {hasChanges ? (
        <div className="sticky bottom-4 z-30 mt-8">
          <div className="flex flex-col gap-3 rounded-xl border border-(--warning)/40 bg-card/95 px-4 py-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-2.5 text-sm font-medium text-foreground">
              <span
                className="size-2 shrink-0 rounded-full bg-(--warning)"
                aria-hidden="true"
              />
              You have unsaved changes
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleDiscard}
                disabled={saving}
              >
                Discard
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <IconLoader2
                      className="mr-2 h-4 w-4 animate-spin"
                      aria-hidden="true"
                    />
                    Saving
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
          {hasAnyConfigured ? (
            <>
              <IconCheck
                size={15}
                className="text-(--gain)"
                aria-hidden="true"
              />
              All alert preferences are saved.
            </>
          ) : (
            <>No delivery channel is turned on yet. Enable one to receive Watch alerts.</>
          )}
        </div>
      )}
    </main>
  );
};

export default NotificationsTab;
